import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Search, Loader2, Plus, X } from "lucide-react";

import InstallerShell from "@/components/installer/InstallerShell";
import OperationsShell from "@/components/operations/OperationsShell";
import MasterDetailLayout from "@/components/layout/MasterDetailLayout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import BulkActionBar from "@/components/bulk/BulkActionBar";
import BulkDeleteModal from "@/components/bulk/BulkDeleteModal";
import PermanentPurgeModal from "@/components/bulk/PermanentPurgeModal";
import { reportBulkDeleteResult } from "@/components/bulk/reportBulkDelete";

import UserFilters, {
    DEFAULT_FILTERS, applyUserFilters, computeUserFilterCounts,
} from "./admin-security/UserFilters";
import UserListItem from "./admin-security/UserListItem";
import UserDetailPanel from "./admin-security/UserDetailPanel";
import SessionsCard from "./admin-security/SessionsCard";
import AuditTab from "./admin-security/AuditTab";
import UserFormModal from "./admin-security/UserFormModal";
import TempPasswordModal from "./admin-security/TempPasswordModal";
import ConfirmDialog from "./admin-security/ConfirmDialog";

/**
 * `/settings/security` — Security Administration workspace.
 *
 * Rebuilt around the standard Installer Portal master/detail layout so
 * the console fills iPad landscape and desktop widths cleanly. Detail
 * pane exposes Overview / Sessions / Audit tabs; every mutation goes
 * through a tone-aware ConfirmDialog because the backend also enforces
 * `confirm: true` on the body.
 */
export default function AdminSecurity() {
    const { user } = useAuth();
    // OPS-CUST-001 · Minimal deep-link support: `?user=<id>` pre-selects
    // a user in the roster. Backward-compatible — omitting the param
    // preserves the existing behaviour (no initial selection).
    const [searchParams, setSearchParams] = useSearchParams();
    const deepLinkUserId = searchParams.get("user");
    const [users, setUsers] = useState([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(deepLinkUserId || null);
    const [tab, setTab] = useState("overview");
    const [filters, setFilters] = useState(DEFAULT_FILTERS);

    // Consume the deep-link once so subsequent selection changes don't
    // rewrite the URL — the SPA state stays canonical after landing.
    useEffect(() => {
        if (deepLinkUserId) {
            setSelectedId(deepLinkUserId);
            const next = new URLSearchParams(searchParams);
            next.delete("user");
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try { setUsers(await api.adminListUsers(query)); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load users"); }
        finally { setLoading(false); }
    }, [query]);

    useEffect(() => { load(); }, [load]);

    // Chip counts derive from the search-narrowed set so a chip shows how
    // many rows in the *current search* match, not how many rows exist
    // in the whole database.
    const counts = useMemo(() => computeUserFilterCounts(users), [users]);
    const visible = useMemo(
        () => applyUserFilters(users, filters),
        [users, filters],
    );

    const selected = useMemo(
        () => visible.find((u) => u.id === selectedId)
              || users.find((u) => u.id === selectedId)
              || null,
        [visible, users, selectedId],
    );

    // ─── Bulk delete ────────────────────────────────────────────────────
    const eligibleForBulk = useCallback(
        (u) => u.id !== user?.id && u.account_status !== "deleted",
        [user?.id],
    );
    const visibleEligibleIds = useMemo(
        () => visible.filter(eligibleForBulk).map((u) => u.id),
        [visible, eligibleForBulk],
    );
    const bulk = useBulkSelection(visibleEligibleIds);
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [purgeModalOpen, setPurgeModalOpen] = useState(false);

    useEffect(() => {
        bulk.pruneToKnown(users.map((u) => u.id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users]);

    async function handleBulkDone(result) {
        reportBulkDeleteResult(result, { labelSingular: "user account", labelPlural: "user accounts" });
        bulk.clearAll();
        setBulkModalOpen(false);
        await load();
    }
    async function handlePurgeDone(result) {
        if (result && (result.deleted || result.stopped)) {
            reportBulkDeleteResult(result, {
                labelSingular: "test user purged", labelPlural: "test users purged",
            });
        }
        bulk.clearAll();
        setPurgeModalOpen(false);
        await load();
    }

    // ─── Actions + confirm ──────────────────────────────────────────────
    const [confirm, setConfirm] = useState(null); // {kind,label,id,tone,run}
    const [busyKey, setBusyKey] = useState(null);

    const kindApi = {
        unlock:         api.adminUnlock,
        suspend:        api.adminSuspend,
        reactivate:     api.adminReactivate,
        disable:        api.adminDisable,
        enable:         api.adminEnable,
        revoke:         api.adminRevokeSessions,
        force:          api.adminForcePasswordChange,
        resetDemoPw:    api.adminResetToDemoPassword,
        resetPw:        api.adminInitiatePasswordReset,
        grantSecAdmin:  api.adminGrantSecurityAdmin,
        revokeSecAdmin: api.adminRevokeSecurityAdmin,
    };

    async function runConfirm() {
        if (!confirm) return;
        setBusyKey(`${confirm.kind}:${confirm.id || "*"}`);
        try {
            if (confirm.run) {
                await confirm.run();
            } else if (confirm.kind === "resetPassword") {
                // DEV-013A · admin password reset returns a temporary
                // password to display ONCE. Never route this through the
                // standard `updated?.id` merge path — the response is a
                // credential envelope, not a user document.
                const target = users.find((u) => u.id === confirm.id);
                const result = await api.adminResetPassword(confirm.id);
                setTempPasswordInfo({
                    user: target || { id: confirm.id },
                    temporary_password: result.temporary_password,
                });
                // Reflect the flag flip immediately so the Overview grid
                // shows "Force password change: Yes" without a reload.
                setUsers((prev) => prev.map((u) => (
                    u.id === confirm.id
                        ? { ...u, force_password_change: true }
                        : u
                )));
                toast.success("Temporary password issued");
            } else {
                const call = kindApi[confirm.kind];
                if (!call) return;
                const updated = await call(confirm.id);
                if (updated?.id) {
                    setUsers((prev) =>
                        prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)),
                    );
                } else {
                    await load();
                }
                toast.success("Done");
            }
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Action failed");
        } finally {
            setBusyKey(null);
            setConfirm(null);
        }
    }

    // ─── Modals ────────────────────────────────────────────────────────
    const [creatingOpen, setCreatingOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    // DEV-013A · after an admin resets a user's password we display the
    // freshly-minted temporary value ONCE via this modal.
    const [tempPasswordInfo, setTempPasswordInfo] = useState(null); // { user, temporary_password }

    if (!user?.is_security_admin) {
        return (
            <div className="p-10 text-center text-sm text-[#4B5565]">
                You don't have permission to view this page.
            </div>
        );
    }

    const hasFiltersActive =
        filters.role !== "any" || filters.status !== "any" || filters.flags.size > 0 || query.trim().length > 0;

    // ── Toolbar ───────────────────────────────────────────────────────
    const toolbar = (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <div className="relative flex-1 max-w-xl">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5565]" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search users by email or name…"
                        data-testid="admin-sec-search"
                        className="w-full h-10 pl-9 pr-9 rounded-md border border-[#E5E7EB] bg-white text-sm outline-none focus:border-[#3E9E2E] focus:ring-1 focus:ring-[#3E9E2E]/30"
                    />
                    {query && (
                        <button
                            type="button"
                            onClick={() => setQuery("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-[#4B5565] hover:text-[#131B2F]"
                            aria-label="Clear search"
                            data-testid="admin-sec-search-clear"
                        >
                            <X size={12} />
                        </button>
                    )}
                </div>
                {hasFiltersActive && (
                    <button
                        type="button"
                        onClick={() => { setFilters(DEFAULT_FILTERS); setQuery(""); }}
                        data-testid="admin-sec-clear-filters"
                        className="h-10 px-3 rounded-md border border-[#E5E7EB] bg-white text-[11px] text-[#4B5565] hover:text-[#131B2F] hover:bg-[#F5F5F7]"
                    >
                        Clear all
                    </button>
                )}
            </div>
            <UserFilters filters={filters} setFilters={setFilters} counts={counts} />
        </div>
    );

    // ── List summary strip ────────────────────────────────────────────
    const listSummary = (
        <div className="flex items-center flex-wrap gap-3" data-testid="bulk-users-selection-summary">
            <span data-testid="bulk-users-total-selected">
                {bulk.count > 0
                    ? `${bulk.count} of ${visibleEligibleIds.length} selected`
                    : `${visible.length} ${visible.length === 1 ? "user" : "users"}${
                        visible.length !== users.length ? ` (of ${users.length})` : ""
                      }`}
            </span>
            {bulk.count === 0 && visibleEligibleIds.length > 0 && (
                <button
                    type="button"
                    onClick={bulk.selectAllVisible}
                    data-testid="bulk-users-select-all-matching"
                    className="text-[#3E9E2E] hover:underline"
                >
                    Select all {visibleEligibleIds.length}
                </button>
            )}
            {bulk.count > 0 && (
                <button
                    type="button"
                    onClick={bulk.clearAll}
                    data-testid="bulk-users-selection-clear"
                    className="text-[#4B5565] hover:underline"
                >
                    Clear
                </button>
            )}
        </div>
    );

    // ── List body ────────────────────────────────────────────────────
    const list = loading ? (
        <div className="p-10 text-center text-xs text-[#4B5565]">
            <Loader2 size={14} className="inline animate-spin" /> Loading users…
        </div>
    ) : visible.length === 0 ? (
        <div className="p-10 text-center text-xs text-[#4B5565]">
            {hasFiltersActive ? "No users match the current filters." : "No users yet."}
        </div>
    ) : (
        <div data-testid="admin-sec-user-list">
            {visible.map((u) => {
                const disabledReason = u.id === user?.id
                    ? "You cannot delete your own account"
                    : u.account_status === "deleted"
                        ? "Account already deleted"
                        : null;
                return (
                    <UserListItem
                        key={u.id}
                        user={u}
                        selected={selectedId === u.id}
                        onSelect={(id) => { setSelectedId(id); setTab("overview"); }}
                        bulk={bulk}
                        canBulk={eligibleForBulk(u)}
                        disabledReason={disabledReason}
                    />
                );
            })}
        </div>
    );

    // ── Detail body ──────────────────────────────────────────────────
    const detail = selected ? (
        <UserDetailPanel
            user={selected}
            tab={tab}
            setTab={setTab}
            onEdit={() => setEditingUser(selected)}
            onAction={(a) => setConfirm(a)}
            renderSessions={() => (
                <SessionsCard userId={selected.id} onConfirm={setConfirm} />
            )}
            renderAudit={() => (
                <AuditTab userId={selected.id} userLabel={selected.name || selected.email} />
            )}
        />
    ) : (
        <div className="h-full flex items-center justify-center p-10 text-center">
            <div>
                <div className="w-14 h-14 mx-auto rounded-full bg-white border border-[#E5E7EB] flex items-center justify-center text-[#4B5565] mb-3">
                    <Shield size={22} strokeWidth={1.5} />
                </div>
                <div className="text-sm text-[#131B2F] font-medium">Select a user</div>
                <div className="text-xs text-[#4B5565] mt-1">
                    Pick anyone from the list to see their overview, sessions and audit trail.
                </div>
            </div>
        </div>
    );

    // ── Page ──────────────────────────────────────────────────────────
    // Operations users get the OperationsShell chrome so the Security
    // console feels like a natural drill-in from `/operations/administration`
    // rather than an installer-branded detour. Installer sec-admins (the
    // legacy path) continue to see the InstallerShell.
    const Shell = user?.role === "operations" ? OperationsShell : InstallerShell;
    const backHref = user?.role === "operations" ? "/operations/administration" : "/installer";
    const backLabel = user?.role === "operations" ? "Back to Administration" : "Back to dashboard";
    return (
        <Shell>
            <div
                className="h-[calc(100vh-3.5rem)] sm:h-screen"
                data-testid="admin-security-page"
            >
                <MasterDetailLayout
                    eyebrow="Settings"
                    title="Security Administration"
                    icon={Shield}
                    backHref={backHref}
                    backLabel={backLabel}
                    primaryAction={
                        <button
                            type="button"
                            onClick={() => setCreatingOpen(true)}
                            data-testid="admin-sec-new-user"
                            className="h-10 px-4 rounded-md bg-[#131B2F] text-white text-sm font-medium hover:bg-[#0B1220] inline-flex items-center gap-1.5"
                        >
                            <Plus size={14} /> New user
                        </button>
                    }
                    toolbar={toolbar}
                    listSummary={listSummary}
                    list={list}
                    detail={detail}
                    hasSelection={!!selected}
                    onClearSelection={() => setSelectedId(null)}
                />
            </div>

            {/* Confirmations */}
            <ConfirmDialog
                open={!!confirm}
                message={confirm?.label}
                tone={confirm?.tone}
                busy={!!busyKey}
                onCancel={() => setConfirm(null)}
                onConfirm={runConfirm}
            />

            {/* Bulk action bar + modals */}
            <BulkActionBar
                count={bulk.count}
                labelSingular="user account"
                labelPlural="user accounts"
                onClear={bulk.clearAll}
                onDelete={() => setBulkModalOpen(true)}
                secondaryAction={{
                    label: "Permanent purge",
                    tone: "danger",
                    testid: "bulk-users-purge",
                    onClick: () => setPurgeModalOpen(true),
                }}
                testidPrefix="bulk-users"
            />
            <BulkDeleteModal
                open={bulkModalOpen}
                onCancel={() => setBulkModalOpen(false)}
                onDone={handleBulkDone}
                ids={bulk.ids}
                labelSingular="user account"
                labelPlural="user accounts"
                deleter={(batch, ctx) => api.bulkDeleteUsers(batch, ctx)}
                previewFetcher={(batch) => api.bulkDeleteUsersPreview(batch)}
                extraWarning="This is a cascade delete: every project, task, appointment, calendar entry, journey, notification, message and document that belongs solely to the selected user(s) will be removed at the same time. Audit history is retained with a 'Deleted user' label."
                testidPrefix="bulk-users-delete"
            />
            <PermanentPurgeModal
                open={purgeModalOpen}
                userIds={bulk.ids}
                onCancel={() => setPurgeModalOpen(false)}
                onDone={handlePurgeDone}
                testidPrefix="bulk-users-purge"
            />

            <UserFormModal
                open={creatingOpen}
                mode="create"
                onCancel={() => setCreatingOpen(false)}
                onSaved={async () => { setCreatingOpen(false); await load(); }}
            />
            <UserFormModal
                open={!!editingUser}
                mode="edit"
                user={editingUser}
                onCancel={() => setEditingUser(null)}
                onSaved={async () => { setEditingUser(null); await load(); }}
            />

            <TempPasswordModal
                open={!!tempPasswordInfo}
                user={tempPasswordInfo?.user}
                temporaryPassword={tempPasswordInfo?.temporary_password}
                onClose={() => setTempPasswordInfo(null)}
            />
        </Shell>
    );
}
